import type { CalibrationFlow, CalibrationFlowState } from '../../interaction/calibration';
import type { CalibrationStepState } from '../../interaction/calibration';
import {
  CALIBRATION_STEP_LABELS,
  INVENTED_CALIBRATION_COPY,
} from '../../platform/config/calibration';

/**
 * Hero training's controls and progress, without the screen around them (Slice G).
 *
 * Extracted from {@link CalibrationPage} rather than copied, because Slice G gives training a
 * *second* home: a grown-up's `RESTART_CALIBRATION` override runs it over a paused mission (GDS §32,
 * D-041(1)). Two copies of a child-facing sequence would be two chances to drift — a prompt worded
 * one way on one screen and another way on the other, a step that is skippable in one place and not
 * in the other.
 *
 * It renders what the flow reports and nothing else: statuses, feedback codes and a prompt, all in
 * the child's own words from the config layer (D-061(4)). It owns no camera, no lifecycle and no
 * navigation — whoever mounts it decides what training *is* in that context.
 */
export interface CalibrationTrainerProps {
  flow: CalibrationFlow;
  state: CalibrationFlowState;
  /** Body-position guidance in the readiness screen's own words, when there is any. */
  guidance?: string | null;
  /** Whatever "stopping" means where this is mounted — leaving the screen, or backing out. */
  children?: React.ReactNode;
}

export function CalibrationTrainer({
  flow,
  state,
  guidance = null,
  children = null,
}: CalibrationTrainerProps) {
  const activeStep = state.steps.find((step) => step.id === state.activeStepId) ?? null;

  return (
    <>
      <p
        className="camera-check__headline"
        data-testid="calibration-prompt"
        role="status"
        aria-live="polite"
      >
        <span data-testid="calibration-copy">
          {activeStep === null ? '' : INVENTED_CALIBRATION_COPY.prompts[activeStep.id]}
        </span>
      </p>

      <p
        className="readiness__message"
        data-testid="calibration-feedback"
        role="status"
        aria-live="polite"
      >
        <span data-testid="calibration-copy">
          {activeStep === null ? '' : INVENTED_CALIBRATION_COPY.feedback[activeStep.feedback]}
        </span>
      </p>

      {guidance === null ? null : (
        <p className="readiness__message" data-testid="calibration-guidance">
          {guidance}
        </p>
      )}

      <ul className="readiness calibration__steps" data-testid="calibration-steps">
        {state.steps.map((step) => (
          <li
            key={step.id}
            data-testid="calibration-step"
            data-step-id={step.id}
            data-status={step.status}
          >
            <span className="readiness__label">{CALIBRATION_STEP_LABELS[step.id]}</span>
            <StepProgress step={step} />
          </li>
        ))}
      </ul>

      <p className="calibration__controls">
        {activeStep?.id === 'confirm' ? (
          <button
            type="button"
            className="button button--primary"
            data-testid="calibration-confirm"
            onClick={(event) => {
              flow.confirm();
              event.currentTarget.blur();
            }}
          >
            <span data-testid="calibration-copy">{INVENTED_CALIBRATION_COPY.confirm}</span>
          </button>
        ) : null}

        {activeStep?.skippable === true ? (
          <button
            type="button"
            className="button"
            data-testid="calibration-skip"
            onClick={(event) => {
              flow.skipStep();
              event.currentTarget.blur();
            }}
          >
            <span data-testid="calibration-copy">{INVENTED_CALIBRATION_COPY.skip}</span>
          </button>
        ) : null}

        <button
          type="button"
          className="button"
          data-testid="calibration-restart"
          onClick={(event) => {
            flow.restart();
            event.currentTarget.blur();
          }}
        >
          <span data-testid="calibration-copy">{INVENTED_CALIBRATION_COPY.restart}</span>
        </button>

        {children}
      </p>
    </>
  );
}

/** How far through a step the child is, as a bar rather than a number they would have to read. */
function StepProgress({ step }: { step: CalibrationStepState }) {
  return (
    <span
      className="calibration__progress"
      data-testid="calibration-step-progress"
      aria-hidden="true"
      style={
        { '--calibration-progress': `${Math.round(step.progress * 100)}%` } as React.CSSProperties
      }
    />
  );
}
